/**
 * REPORT MODAL
 *
 * Dialog that renders a report for a specific scope. The scope (project /
 * release / scenario) is fixed at open time via `initialLevel` + the matching
 * id prop — no level-switcher in the toolbar. This keeps the surface focused:
 * if you opened the modal from a scenario, you only see the scenario report.
 *
 * Project-level reports are opened from the Release List page header. Release-
 * level reports are opened from release cards. Scenario-level reports are
 * opened from the scenario detail page.
 *
 * Filter bar is intentionally not rendered in the UI — the backend still
 * accepts filter params via the `ReportFilters` state (kept in Redux) so the
 * API surface is unchanged, but we don't expose the controls to users yet.
 */

import React, { useEffect } from 'react'
import { Dialog } from '@headlessui/react'
import { useDispatch, useSelector } from 'react-redux'
import Button from '@atlaskit/button'
import Tooltip from '@atlaskit/tooltip'
import CrossIcon from '@atlaskit/icon/glyph/cross'
import GraphLineIcon from '@atlaskit/icon/glyph/graph-line'

import ProjectReport from './ProjectReport'
import ReleaseReport from './ReleaseReport'
import ScenarioReport from './ScenarioReport'
import ExportButton from './export/ExportButton'
import DrillDownPanel from './drilldown/DrillDownPanel'

import {
  setReportLevel,
  setReportContext,
  clearReports,
  selectCurrentReportLevel,
  selectReportContext,
  selectProjectReport,
  selectReleaseReport,
  selectScenarioReport,
  selectReleasesSummary
} from '../../store/slices/reportsSlice'

const TITLE_BY_LEVEL = {
  project: 'Project Report',
  release: 'Release Report',
  scenario: 'Scenario Report'
}

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {'project'|'release'|'scenario'} props.initialLevel - Fixed scope the modal opens in.
 * @param {number|string} [props.projectId]   - Jira project id (required for project / scenario levels for header context)
 * @param {number|string} [props.releaseId]   - Release id (required for release level)
 * @param {number|string} [props.scenarioId]  - Scenario id (required for scenario level)
 */
const ReportModal = ({
  isOpen,
  onClose,
  initialLevel = 'scenario',
  projectId,
  releaseId,
  scenarioId
}) => {
  const dispatch = useDispatch()

  const currentLevel = useSelector(selectCurrentReportLevel)
  const context = useSelector(selectReportContext)
  const projectReport = useSelector(selectProjectReport)
  const releaseReport = useSelector(selectReleaseReport)
  const scenarioReport = useSelector(selectScenarioReport)
  const releasesSummary = useSelector(selectReleasesSummary)

  // Seed scope + level into Redux each time the modal is (re)opened.
  useEffect(() => {
    if (isOpen) {
      dispatch(setReportContext({ projectId, releaseId, scenarioId }))
      dispatch(setReportLevel(initialLevel))
    }
  }, [isOpen, dispatch, projectId, releaseId, scenarioId, initialLevel])

  // Clear fetched report data a beat after closing so the next open starts fresh.
  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        dispatch(clearReports())
      }, 200)
      return () => clearTimeout(timeout)
    }
  }, [isOpen, dispatch])

  const getCurrentReportData = () => {
    switch (currentLevel) {
      case 'project':
        return projectReport
      case 'release':
        return releaseReport
      case 'scenario':
        return scenarioReport
      default:
        return null
    }
  }

  const getEntityName = () => {
    switch (currentLevel) {
      case 'project':
        return projectReport?.projectName
      case 'release':
        return releaseReport?.releaseName
      case 'scenario':
        return scenarioReport?.scenarioName
      default:
        return 'report'
    }
  }

  // Scope-to-availability checks — we only render the level matching initialLevel,
  // so these gate the "no context" fallback when someone opens the modal without ids.
  const hasProjectContext = !!projectId
  const hasReleaseContext = !!releaseId
  const hasScenarioContext = !!scenarioId
  const hasAnyContext = hasProjectContext || hasReleaseContext || hasScenarioContext

  return (
    <Dialog open={isOpen} onClose={onClose} className="headless-dialog-root">
      {/* Backdrop */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none',
          padding: '24px'
        }}
      >
        <Dialog.Panel
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            // Wider dialog — reports have charts + tables that benefit from the
            // extra horizontal room. 1180px keeps us well under typical laptop
            // widths while leaving breathing room on the sides.
            width: '1180px',
            maxWidth: '100%',
            maxHeight: 'calc(100vh - 48px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'auto'
          }}
        >
          {/* HEADER — Title + Export + Close. No level tabs, no filter controls. */}
          <div
            style={{
              padding: '14px 24px',
              borderBottom: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
              gap: '12px'
            }}
          >
            {/* Left: Title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <GraphLineIcon label="" size="medium" primaryColor="var(--ds-icon, #6B778C)" />
              <Dialog.Title
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: 'var(--ds-text, #172B4D)',
                  margin: 0
                }}
              >
                {TITLE_BY_LEVEL[initialLevel] || 'Execution Report'}
              </Dialog.Title>
            </div>

            {/* Right: Export + Close */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ExportButton
                reportData={getCurrentReportData()}
                level={currentLevel}
                entityName={getEntityName()}
                releasesSummary={currentLevel === 'project' ? releasesSummary : null}
                appearance="subtle"
              />
              <Tooltip content="Close">
                <Button
                  appearance="subtle"
                  iconBefore={<CrossIcon label="Close" size="medium" />}
                  onClick={onClose}
                />
              </Tooltip>
            </div>
          </div>

          {/* CONTENT */}
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: '24px'
            }}
          >
            {initialLevel === 'scenario' && hasScenarioContext && (
              <ScenarioReport scenarioId={context.scenarioId || scenarioId} />
            )}
            {initialLevel === 'release' && hasReleaseContext && (
              <ReleaseReport releaseId={context.releaseId || releaseId} />
            )}
            {initialLevel === 'project' && hasProjectContext && (
              <ProjectReport projectId={context.projectId || projectId} />
            )}

            {!hasAnyContext && (
              <div
                style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: 'var(--ds-text-subtle, #6B778C)'
                }}
              >
                No report context available
              </div>
            )}
          </div>
        </Dialog.Panel>
      </div>

      {/* Drill-down panel floats on top of the modal when a chart segment is clicked. */}
      <DrillDownPanel />
    </Dialog>
  )
}

export default ReportModal
